import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Button,
  Badge,
  Spinner,
  Alert,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { useAlertRules } from '../../Hooks/useAlertRules';
import {
  COMMON_FIELDS,
  FIELD_DEFS,
  RULE_FIELDS,
  TYPE_LABELS,
  getByPath,
  setByPath,
} from './alertRuleFields';

const SEVERITIES = [
  { value: 'CRITICA', label: 'Crítica' },
  { value: 'ADVERTENCIA', label: 'Advertencia' },
  { value: 'INFORMATIVA', label: 'Informativa' },
];

const SEVERITY_COLOR = {
  CRITICA: 'danger',
  ADVERTENCIA: 'warning',
  INFORMATIVA: 'info',
};

/** Un campo del formulario, resuelto desde su definición. */
const RuleField = ({ fieldKey, draft, onChange }) => {
  const def = FIELD_DEFS[fieldKey];
  if (!def) return null;

  const value = getByPath(draft, def.path);

  const handle = (next) => onChange(setByPath(draft, def.path, next));

  if (def.type === 'switch') {
    return (
      <FormGroup check className="mb-3">
        <Input
          type="checkbox"
          id={fieldKey}
          checked={Boolean(value)}
          onChange={(e) => handle(e.target.checked)}
        />
        <Label check for={fieldKey} className="small fw-semibold">
          {def.label}
        </Label>
        {def.help && <FormText className="d-block">{def.help}</FormText>}
      </FormGroup>
    );
  }

  if (def.type === 'multiselect') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <FormGroup className="mb-3">
        <Label className="small fw-semibold">{def.label}</Label>
        <div className="d-flex gap-3">
          {def.options.map((option) => (
            <FormGroup check key={option.value}>
              <Input
                type="checkbox"
                id={`${fieldKey}-${option.value}`}
                checked={selected.includes(option.value)}
                onChange={(e) =>
                  handle(
                    e.target.checked
                      ? [...selected, option.value]
                      : selected.filter((v) => v !== option.value)
                  )
                }
              />
              <Label check for={`${fieldKey}-${option.value}`} className="small">
                {option.label}
              </Label>
            </FormGroup>
          ))}
        </div>
        {def.help && <FormText>{def.help}</FormText>}
      </FormGroup>
    );
  }

  return (
    <FormGroup className="mb-3">
      <Label className="small fw-semibold">{def.label}</Label>
      {def.type === 'select' ? (
        <Input
          type="select"
          bsSize="sm"
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
        >
          <option value="">Sin definir</option>
          {def.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      ) : (
        <Input
          type={def.type}
          bsSize="sm"
          min={def.min}
          max={def.max}
          value={value ?? ''}
          onChange={(e) =>
            handle(
              def.type === 'number'
                ? e.target.value === ''
                  ? null
                  : Number(e.target.value)
                : e.target.value
            )
          }
        />
      )}
      {def.help && <FormText>{def.help}</FormText>}
    </FormGroup>
  );
};

/**
 * Parametrización de alertas e insights.
 *
 * Cada regla es un umbral del motor: cuántos intentos fallidos, qué porcentaje
 * de caída, cuántos días de ventana. El motor las lee en cada corrida, así que
 * guardar aquí cambia el comportamiento sin desplegar.
 */
const AlertRulesModal = ({ isOpen, toggle, initialCode = null }) => {
  const {
    rules,
    isLoading,
    error,
    updateRule,
    toggleRule,
    removeRule,
    isSaving,
    saveError,
  } = useAlertRules({ enabled: isOpen });

  const [selectedCode, setSelectedCode] = useState(null);
  const [draft, setDraft] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const selected = rules.find((rule) => rule.code === selectedCode) ?? null;

  // Al elegir otra regla se descarta el borrador anterior.
  useEffect(() => {
    setDraft(selected ? JSON.parse(JSON.stringify(selected)) : null);
    setSavedAt(null);
  }, [selectedCode, rules.length]);

  // Al abrir desde "Configurar" de un detector concreto, ese detector queda
  // seleccionado; desde el botón general, el primero de la lista.
  // `selectedCode` NO va en las dependencias a propósito: si estuviera, elegir
  // otra regla dentro del modal dispararía este efecto y lo devolvería a
  // `initialCode` en el acto.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedCode(initialCode ?? rules[0]?.code ?? null);
  }, [isOpen, initialCode, rules.length]);

  const handleSave = async () => {
    if (!draft) return;
    await updateRule(draft.id || draft._id, {
      name: draft.name,
      description: draft.description,
      threshold: draft.threshold,
      threshold_secondary: draft.threshold_secondary,
      period_days: draft.period_days,
      comparison_period: draft.comparison_period,
      severity: draft.severity,
      min_subject_volume: draft.min_subject_volume,
      auto_resolve_days: draft.auto_resolve_days,
      notify_email: draft.notify_email,
      email_recipients: draft.email_recipients,
      params: draft.params,
    });
    setSavedAt(new Date());
  };

  const fields = draft ? RULE_FIELDS[draft.type] ?? [] : [];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>Configuración de alertas e insights</ModalHeader>
      <ModalBody>
        {isLoading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        )}

        {error && <Alert color="danger">{error.message}</Alert>}
        {saveError && <Alert color="danger">{saveError.message}</Alert>}

        {!isLoading && !error && (
          <Row>
            <Col md="4">
              <h6 className="fw-bold mb-2">Reglas</h6>
              <ListGroup flush>
                {rules.map((rule) => (
                  <ListGroupItem
                    key={rule.code}
                    action
                    active={rule.code === selectedCode}
                    onClick={() => setSelectedCode(rule.code)}
                    className="px-2 py-2"
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div className="small fw-semibold">{rule.name}</div>
                        <div
                          className={
                            rule.code === selectedCode
                              ? 'small text-white-50'
                              : 'small text-muted'
                          }
                        >
                          {TYPE_LABELS[rule.type] ?? rule.type}
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-end gap-1">
                        {/* Alerta vs insight: sin esto no había forma de saber
                            qué produce cada regla. */}
                        <Badge
                          color={rule.kind === 'INSIGHT' ? 'info' : 'warning'}
                          pill
                        >
                          {rule.kind === 'INSIGHT' ? 'Insight' : 'Alerta'}
                        </Badge>
                        <Badge color={rule.active ? 'success' : 'secondary'} pill>
                        {rule.active ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </div>
                    </div>
                  </ListGroupItem>
                ))}
              </ListGroup>
            </Col>

            <Col md="8">
              {!draft && (
                <p className="text-muted">Seleccione una regla para editarla.</p>
              )}

              {draft && (
                <Card className="border-0">
                  <CardBody className="p-0">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="fw-bold mb-1">{draft.name}</h6>
                        <div className="small text-muted">
                          <code>{draft.code}</code> ·{' '}
                          {draft.kind === 'INSIGHT' ? 'Insight' : 'Alerta'} ·{' '}
                          {TYPE_LABELS[draft.type] ?? draft.type}
                          {draft.is_default && ' · regla del sistema'}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        color={draft.active ? 'outline-secondary' : 'outline-success'}
                        disabled={isSaving}
                        onClick={() => toggleRule(draft.id || draft._id)}
                      >
                        {draft.active ? 'Desactivar' : 'Activar'}
                      </Button>
                    </div>

                    <Form>
                      <FormGroup className="mb-3">
                        <Label className="small fw-semibold">
                          Nombre (se usa como título de la alerta)
                        </Label>
                        <Input
                          bsSize="sm"
                          value={draft.name ?? ''}
                          onChange={(e) =>
                            setDraft({ ...draft, name: e.target.value })
                          }
                        />
                      </FormGroup>

                      <FormGroup className="mb-3">
                        <Label className="small fw-semibold">Descripción</Label>
                        <Input
                          type="textarea"
                          bsSize="sm"
                          rows={2}
                          value={draft.description ?? ''}
                          onChange={(e) =>
                            setDraft({ ...draft, description: e.target.value })
                          }
                        />
                      </FormGroup>

                      <Row>
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <Label className="small fw-semibold">Severidad</Label>
                            <Input
                              type="select"
                              bsSize="sm"
                              value={draft.severity ?? ''}
                              onChange={(e) =>
                                setDraft({ ...draft, severity: e.target.value })
                              }
                            >
                              {SEVERITIES.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.label}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <div className="pt-4">
                            <Badge color={SEVERITY_COLOR[draft.severity] ?? 'secondary'}>
                              {SEVERITIES.find((s) => s.value === draft.severity)
                                ?.label ?? draft.severity}
                            </Badge>
                          </div>
                        </Col>
                      </Row>

                      <hr />
                      <div className="small fw-bold text-muted mb-2">
                        UMBRALES DE ESTE DETECTOR
                      </div>

                      <Row>
                        {fields.map((fieldKey) => (
                          <Col md="6" key={fieldKey}>
                            <RuleField
                              fieldKey={fieldKey}
                              draft={draft}
                              onChange={setDraft}
                            />
                          </Col>
                        ))}
                      </Row>

                      <hr />
                      <div className="small fw-bold text-muted mb-2">
                        SEGUIMIENTO Y NOTIFICACIÓN
                      </div>

                      <Row>
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <Label className="small fw-semibold">
                              Cerrar automáticamente tras (días)
                            </Label>
                            <Input
                              type="number"
                              bsSize="sm"
                              min={1}
                              value={draft.auto_resolve_days ?? ''}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  auto_resolve_days:
                                    e.target.value === ''
                                      ? null
                                      : Number(e.target.value),
                                })
                              }
                            />
                            <FormText>
                              Días sin volver a detectarse antes de darla por resuelta.
                            </FormText>
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <Label className="small fw-semibold">
                              Destinatarios del resumen
                            </Label>
                            <Input
                              bsSize="sm"
                              placeholder="correo1@dominio.com, correo2@dominio.com"
                              value={(draft.email_recipients ?? []).join(', ')}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  email_recipients: e.target.value
                                    .split(',')
                                    .map((v) => v.trim())
                                    .filter(Boolean),
                                })
                              }
                            />
                            <FormText>
                              Si se deja vacío, el resumen va a los administradores activos.
                            </FormText>
                          </FormGroup>
                        </Col>
                      </Row>

                      <FormGroup check className="mb-3">
                        <Input
                          type="checkbox"
                          id="notify_email"
                          checked={draft.notify_email !== false}
                          onChange={(e) =>
                            setDraft({ ...draft, notify_email: e.target.checked })
                          }
                        />
                        <Label check for="notify_email" className="small fw-semibold">
                          Incluir en el resumen diario por correo
                        </Label>
                      </FormGroup>
                    </Form>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        )}
      </ModalBody>

      <ModalFooter className="justify-content-between">
        <div>
          {draft && !draft.is_default && (
            <Button
              color="link"
              className="text-danger p-0"
              disabled={isSaving}
              onClick={() => removeRule(draft.id || draft._id)}
            >
              Eliminar regla
            </Button>
          )}
          {draft?.is_default && (
            <small className="text-muted">
              Las reglas del sistema no se eliminan, sólo se desactivan.
            </small>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {savedAt && (
            <small className="text-success">
              Guardado {savedAt.toLocaleTimeString('es-CO')}
            </small>
          )}
          <Button color="light" onClick={toggle} disabled={isSaving}>
            Cerrar
          </Button>
          <Button color="primary" onClick={handleSave} disabled={!draft || isSaving}>
            {isSaving ? <Spinner size="sm" /> : 'Guardar cambios'}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default AlertRulesModal;
